import { type MouseEventHandler, useRef, useState } from "react";

import { useMenuOpen } from "mds/components/MenuOpenContext";
import type { PolymorphicProps } from "types";
import { type TrackingProps, trackEvent } from "utils/amplitude";

export type BaseButtonProps<T extends React.ElementType = "button"> = PolymorphicProps<T> &
  TrackingProps & {
    className?: string;
    isSelected?: boolean;
    onClick?: MouseEventHandler<unknown>;
    /**
     * When true, ignores re-clicks and sets `disabled` until `onClick` finishes
     * (including async handlers that return a Promise).
     */
    disableWhilePending?: boolean;
  };

/**
 * You probably do NOT want to use this!
 * This is an abstract base component that other components inherit from.
 * It's better to use one of the child components, like `SquareButton` or `RoundedButton`.
 *
 * If you are editing this component, do NOT add any styling to it. No CSS classes, no styles, no nothing.
 * The child components are responsible for styling.
 */
export const BaseButton = <T extends React.ElementType = "button">({
  as,
  className,
  onClick,
  eventName,
  eventProps = {},
  isSelected,
  disableWhilePending = false,
  disabled,
  "aria-disabled": ariaDisabled,
  ...rest
}: BaseButtonProps<T>) => {
  const Component = as || "button";
  const isMenuOpen = useMenuOpen();
  const selected = isSelected ?? isMenuOpen;
  const isAriaDisabled = ariaDisabled === true || ariaDisabled === "true";
  const [isInFlight, setIsInFlight] = useState(false);
  const inFlightRef = useRef(false);
  const isDisabled = Boolean(disabled) || (disableWhilePending && isInFlight);

  const clickHandler: MouseEventHandler<unknown> | undefined = eventName
    ? (e) => {
        trackEvent(eventName, { eventCategory: "Button press", ...eventProps });
        return onClick?.(e);
      }
    : onClick;

  // If we are using a button, we need to set the type to "button" to prevent
  // it from submitting a form when the button is clicked.
  const buttonProps = Component === "button" ? ({ type: "button" } as const) : {};

  return (
    <Component
      className={className}
      {...buttonProps}
      {...rest}
      aria-disabled={ariaDisabled}
      data-selected={selected || undefined}
      disabled={isDisabled || undefined}
      onClick={(e) => {
        if (isAriaDisabled || isDisabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        if (disableWhilePending) {
          if (inFlightRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          inFlightRef.current = true;
          setIsInFlight(true);

          Promise.resolve(clickHandler?.(e)).finally(() => {
            inFlightRef.current = false;
            setIsInFlight(false);
          });

          return;
        }

        clickHandler?.(e);
      }}
    />
  );
};
